import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Eye, EyeOff, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    const result = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (result.ok) {
      toast.success("Welcome back!");
      navigate("/app", { replace: true });
    } else {
      setError(result.error || "Login failed.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB] px-6 py-10" data-testid="login-page">
      <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-[#1F3A5F] mb-2">Welcome back</h1>
          <p className="text-[#6B7280]">Log in to continue your message coaching.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#2C2C2C]">Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A7B4]" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="pl-10 h-12 rounded-xl bg-white border-[#E5E7EB] focus:border-[#4FB3A5]"
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#2C2C2C]">Password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A7B4]" />
              <Input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="pl-10 pr-10 h-12 rounded-xl bg-white border-[#E5E7EB] focus:border-[#4FB3A5]"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A7B4] hover:text-[#1F3A5F]"
                data-testid="login-toggle-password"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="text-right">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="text-sm text-[#2F5BFF] hover:underline font-medium"
            >
              Forgot password?
            </Link>
          </div>

          {error && (
            <div
              className="text-sm text-[#E07A7A] bg-[#FEF3F2] border border-[#FECACA] p-3 rounded-xl"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-semibold"
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="animate-spin" /> : "Log in"}
          </Button>
        </form>

        <div className="text-center mt-8 text-sm text-[#6B7280]">
          Don't have an account?{" "}
          <Link to="/signup" data-testid="login-signup-link" className="text-[#2F5BFF] font-medium hover:underline">
            Sign up
          </Link>
        </div>

        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => navigate("/app", { replace: true })}
            data-testid="login-guest-btn"
            className="text-sm text-[#6B7280] hover:text-[#1F3A5F]"
          >
            Continue without an account →
          </button>
        </div>
      </div>

      <div className="text-center text-xs text-[#A0A7B4] pt-6 space-x-4">
        <Link to="/privacy" className="hover:text-[#1F3A5F]" data-testid="login-privacy-link">Privacy</Link>
        <Link to="/terms" className="hover:text-[#1F3A5F]" data-testid="login-terms-link">Terms</Link>
      </div>
    </div>
  );
}
